import { useEffect, useState } from "react";
import { api, apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, Send, Clock, CheckCircle2, FileText, Download, Gavel, FileSpreadsheet, FolderArchive, ShieldAlert, SlidersHorizontal, HardDriveUpload, AlertCircle, ExternalLink, CalendarClock } from "lucide-react";

const SLA_FIELDS = [
  ["visit_interval_days", "Visit interval (days)", "An asset is up to date if visited within this many days"],
  ["overdue_after_days", "Overdue after (days)", "Beyond this, the asset is flagged overdue in red"],
  ["escalation_after_days", "Escalate after (days)", "Breaches beyond this are emailed daily to admins and the executive"],
  ["hearing_reminder_days", "Hearing reminder lead (days)", "How many days ahead court hearing reminders go out"],
];

const SCHEDULES = [
  ["Monthly visit reminders", "1st of every month · 9:00 AM IST",
    "Each field executive receives the list of assets due or overdue for a site visit."],
  ["Weekly compliance digest", "Every Monday · 9:00 AM IST",
    "Admins and the company inbox receive portfolio compliance, assets needing attention, plus the latest site photo thumbnails."],
  ["New property alert", "Instantly on onboarding",
    "A confirmation with full asset details is sent to bhoomiatm@gmail.com."],
  ["Encroachment escalation", "Instantly when flagged",
    "If an executive reports encroachment, boundary damage or a dispute notice, admins get the photos, geotag and notes at once."],
  ["Court hearing reminders", "Daily · 8:00 AM IST",
    "Any hearing falling within the reminder lead time is emailed to admins and to the advocate handling that case."],
  ["SLA breach escalation", "Daily · 8:30 AM IST",
    "Assets unvisited beyond your escalation threshold are escalated to admins, and each executive gets their own list."],
  ["Document renewal alerts", "Daily · 8:15 AM IST",
    "Leases, tax receipts, ECs, insurance and NOCs that have lapsed or are inside their reminder window are emailed to admins."],
  ["Monthly Google Drive backup", "1st of every month · 2:00 AM IST",    "One ZIP per property is uploaded to your Drive folder, folders older than 12 months are pruned and admins receive a summary. If Drive is not connected, admins instead get a reminder to download the portfolio ZIP manually."],
];

export default function Notifications() {
  const [busy, setBusy] = useState("");
  const [sla, setSla] = useState(null);
  const [savingSla, setSavingSla] = useState(false);
  const [drive, setDrive] = useState(null);
  const [driveBusy, setDriveBusy] = useState(false);

  const loadDrive = () => api.get("/backup/drive/status")
    .then(({ data }) => setDrive(data)).catch(() => setDrive({ configured: false, needs_setup: true, detail: "Unavailable", history: [] }));

  useEffect(() => {
    loadDrive();
    const p = new URLSearchParams(window.location.search);
    if (p.get("drive_connected")) {
      toast.success("Google Drive connected", { description: "Monthly backups will run on the 1st." });
      window.history.replaceState({}, "", "/notifications");
    } else if (p.get("drive_error")) {
      toast.error("Could not connect Google Drive", { description: p.get("drive_error") });
      window.history.replaceState({}, "", "/notifications");
    }
  }, []);

  const connectDrive = async () => {
    setDriveBusy(true);
    try {
      const { data } = await api.get("/oauth/drive/start");
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setDriveBusy(false);
    }
  };

  const disconnectDrive = async () => {
    try {
      await api.delete("/backup/drive/connection");
      toast.success("Google Drive disconnected");
      loadDrive();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const runDriveBackup = async () => {
    setDriveBusy(true);
    try {
      const { data } = await api.post("/backup/drive/run");
      toast.success(`Backed up ${data.uploaded} of ${data.properties} properties`, {
        description: `Drive folder ${data.month} · summary emailed to admins`,
      });
      loadDrive();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setDriveBusy(false);
    }
  };

  useEffect(() => {
    api.get("/settings/sla").then(({ data }) => setSla(data)).catch(() => {});
  }, []);

  const saveSla = async () => {
    setSavingSla(true);
    try {
      const body = {
        visit_interval_days: Number(sla.visit_interval_days),
        overdue_after_days: Number(sla.overdue_after_days),
        escalation_after_days: Number(sla.escalation_after_days),
        hearing_reminder_days: Number(sla.hearing_reminder_days),
        escalation_enabled: !!sla.escalation_enabled,
      };
      const { data } = await api.put("/settings/sla", body);
      setSla(data);
      toast.success("SLA thresholds saved", { description: "Compliance and alerts now use these values." });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingSla(false);
    }
  };

  const run = async (path, label) => {
    setBusy(path);
    try {
      const { data } = await api.post(path);
      toast.success(`${label} sent`, { description: JSON.stringify(data) });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const actions = [
    ["/notifications/test", "Send test email", Mail],
    ["/notifications/run-reminders", "Run reminders now", Send],
    ["/notifications/run-digest", "Send digest now", Send],
    ["/notifications/run-hearing-reminders", "Hearing reminders", Gavel],
    ["/notifications/run-sla-escalation", "Run SLA escalation", ShieldAlert],
    ["/notifications/run-document-expiry", "Renewal alerts", CalendarClock],
  ];

  return (
    <div className="p-5 md:p-10 max-w-[1000px] rise" data-testid="notifications-page">
      <p className="label-tiny">Automation</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter">Email notifications</h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
        All automated mail is sent from{" "}
        <span className="font-mono text-foreground">bhoomiatm@gmail.com</span> over Gmail SMTP.
      </p>

      <div className="mt-8 surface divide-y divide-border" data-testid="schedule-list">
        {SCHEDULES.map(([title, when, desc]) => (
          <div key={title} className="p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <CheckCircle2 size={19} className="text-emerald-500 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold">{title}</p>
              <p className="text-xs text-muted-foreground mt-1">{desc}</p>
            </div>
            <span className="label-tiny whitespace-nowrap flex items-center gap-1.5">
              <Clock size={13} /> {when}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {actions.map(([path, label, Icon]) => (
          <Button key={path} variant="outline" data-testid={
            path.includes("test") ? "send-test-email-button"
              : path.includes("sla") ? "run-sla-escalation-button"
              : path.includes("expiry") ? "run-document-expiry-button"
              : path.includes("hearing") ? "run-hearing-reminders-button"
              : path.includes("reminders") ? "run-reminders-button" : "run-digest-button"
          }
            disabled={busy === path} onClick={() => run(path, label)}
            className="h-auto py-5 flex-col gap-2 rounded-xl hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-200">
            <Icon size={19} />
            <span className="text-sm font-medium">{busy === path ? "Sending…" : label}</span>
          </Button>
        ))}
      </div>

      {sla && (
        <div className="mt-10" data-testid="sla-settings">
          <p className="label-tiny">Escalation policy</p>
          <h2 className="mt-2 font-display text-xl font-bold tracking-tight flex items-center gap-2">
            <SlidersHorizontal size={17} className="text-primary" /> Visit SLA &amp; alert thresholds
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            These thresholds drive the compliance colours across the dashboard, the analytics
            compliance score, reminder emails and daily escalations.
          </p>
          <div className="mt-4 surface p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {SLA_FIELDS.map(([key, label, desc]) => (
                <div key={key}>
                  <label className="text-xs font-semibold" htmlFor={`sla-${key}`}>{label}</label>
                  <Input id={`sla-${key}`} type="number" min="1" data-testid={`sla-${key.replace(/_/g, "-")}-input`}
                    value={sla[key]} onChange={(e) => setSla({ ...sla, [key]: e.target.value })}
                    className="mt-1.5 font-mono" />
                  <p className="mt-1.5 text-xs text-muted-foreground">{desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-5">
              <div className="flex items-center gap-3">
                <Switch id="sla-escalation-enabled" data-testid="sla-escalation-toggle"
                  checked={!!sla.escalation_enabled}
                  onCheckedChange={(v) => setSla({ ...sla, escalation_enabled: v })} />
                <label htmlFor="sla-escalation-enabled" className="text-sm">
                  Send daily SLA breach escalation emails
                </label>
              </div>
              <Button data-testid="save-sla-button" disabled={savingSla} onClick={saveSla}>
                {savingSla ? "Saving…" : "Save thresholds"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10" data-testid="drive-backup-section">
        <p className="label-tiny">Off-site archive</p>
        <h2 className="mt-2 font-display text-xl font-bold tracking-tight flex items-center gap-2">
          <HardDriveUpload size={17} className="text-primary" /> Monthly Google Drive backup
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          On the 1st of every month at 2:00 AM IST, one ZIP per property is uploaded to{" "}
          <span className="font-mono text-foreground">REAM Backups / YYYY-MM /</span> in your Google
          Drive — property details, all documents, litigation and mortgage papers, vaastu files, the
          visit log and the latest visit's photos, plus the Excel registers. Folders older than{" "}
          {drive?.retain_months || 6} months are deleted automatically and admins get a summary email.
        </p>

        {drive && (
          <div className="mt-4 surface p-6">
            {drive.configured ? (
              <div className="flex flex-wrap items-center justify-between gap-4" data-testid="drive-connected">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={19} className="text-emerald-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold">Connected to Google Drive</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Uploading into <span className="font-medium text-foreground">{drive.folder?.name}</span>
                      {drive.account ? ` as ${drive.account}` : ""}
                      {drive.quota?.free != null
                        ? ` · ${(drive.quota.free / 1073741824).toFixed(1)} GB free`
                        : ""}
                    </p>
                    {drive.folder?.webViewLink && (
                      <a href={drive.folder.webViewLink} target="_blank" rel="noreferrer"
                        data-testid="drive-folder-link"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
                        Open the Drive folder <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" data-testid="disconnect-drive-button" onClick={disconnectDrive}>
                    Disconnect
                  </Button>
                  <Button data-testid="run-drive-backup-button" disabled={driveBusy} onClick={runDriveBackup}>
                    {driveBusy ? "Backing up…" : "Back up now"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-start gap-4" data-testid="drive-not-configured">
                <AlertCircle size={19} className="text-amber-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold">
                    {drive.connected ? "Google Drive is unreachable"
                      : drive.needs_setup ? "Google sign-in is not set up yet"
                      : "Google Drive is not connected yet"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">{drive.detail}</p>
                  {drive.needs_setup && (
                    <ol className="mt-3 space-y-1.5 text-xs text-muted-foreground list-decimal pl-4">
                      <li>In Google Cloud Console → APIs &amp; Services → <span className="font-medium text-foreground">OAuth consent screen</span>, choose External, fill the app name and add your Gmail under Test users.</li>
                      <li>Credentials → Create credentials → <span className="font-medium text-foreground">OAuth client ID</span> → Web application. Add the redirect URI shown in your setup email.</li>
                      <li>Send me the Client ID and Client Secret — I'll store them, then you can connect with one click.</li>
                    </ol>
                  )}
                  {!drive.needs_setup && !drive.connected && (
                    <Button className="mt-4" data-testid="connect-drive-button"
                      disabled={driveBusy} onClick={connectDrive}>
                      {driveBusy ? "Opening Google…" : "Connect Google Drive"}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {drive.history?.length > 0 && (
              <div className="mt-6 border-t border-border pt-5" data-testid="drive-history">
                <p className="label-tiny">Recent runs</p>
                <div className="mt-3 divide-y divide-border">
                  {drive.history.map((h, i) => (
                    <div key={i} className="py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs">
                      <span className="font-mono font-semibold">{h.month}</span>
                      <span className="text-muted-foreground">
                        {h.uploaded} of {h.properties} properties · {(h.total_bytes / 1048576).toFixed(1)} MB
                        {h.failed?.length ? ` · ${h.failed.length} failed` : ""}
                      </span>
                      <span className="font-mono text-muted-foreground">{h.finished_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-10">
        <p className="label-tiny">Board reporting</p>
        <h2 className="mt-2 font-display text-xl font-bold tracking-tight">Excel registers</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="exports-list">
          {[
            ["all", "All registers", "Assets, litigation and mortgages in one workbook"],
            ["assets", "Asset register", "Title, location, police station, compliance"],
            ["litigation", "Litigation register", "Cases, advocates and hearing dates"],
            ["mortgages", "Mortgage register", "Banks, amounts and release dates"],
          ].map(([which, title, desc]) => (
            <a key={which} href={`${API}/export/registers.xlsx?which=${which}&auth=${localStorage.getItem("token")}`}
              data-testid={`export-${which}-link`}
              className="surface p-5 hover:-translate-y-1 hover:shadow-md hover:border-primary/40 transition-[transform,box-shadow,border-color] duration-200">
              <FileSpreadsheet size={20} className="text-emerald-600" />
              <p className="mt-3 text-sm font-semibold">{title}</p>
              <p className="mt-1 text-xs text-muted-foreground">{desc}</p>
              <p className="mt-2 text-xs font-medium text-primary">Download .xlsx</p>
            </a>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <p className="label-tiny">Offline backup</p>
        <h2 className="mt-2 font-display text-xl font-bold tracking-tight">Full portfolio backup (ZIP)</h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          One folder per property containing its details, document repository, litigation and mortgage
          papers, vaastu files and site-visit log — plus the Excel registers. Keep a copy on your own
          drive.
        </p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="backup-list">
          {[
            ["false", "Documents backup", "Property details, documents, litigation, mortgages, vaastu, visit log", "backup-docs-link"],
            ["true", "Documents + site photos", "Everything above plus every geotagged photo and video by month (large file)", "backup-media-link"],
          ].map(([media, title, desc, testid]) => (
            <a key={media} href={`${API}/export/backup.zip?include_media=${media}&auth=${localStorage.getItem("token")}`}
              data-testid={testid}
              className="surface p-5 flex gap-4 hover:-translate-y-1 hover:shadow-md hover:border-primary/40 transition-[transform,box-shadow,border-color] duration-200">
              <FolderArchive size={20} className="text-primary shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{title}</p>
                <p className="mt-1 text-xs text-muted-foreground">{desc}</p>
                <p className="mt-2 text-xs font-medium text-primary">Download .zip</p>
              </div>
              <Download size={16} className="text-muted-foreground shrink-0" />
            </a>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <p className="label-tiny">User manuals</p>
        <h2 className="mt-2 font-display text-xl font-bold tracking-tight">Documentation</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="manuals-list">
          {[
            ["admin", "Administrator Manual", "Onboarding, documents, litigation, mortgages, vaastu, compliance and email automation"],
            ["executive", "Field Executive Manual", "Signing in on a phone and recording geotagged site visits"],
          ].map(([role, title, desc]) => (
            <a key={role} href={`${API}/manuals/${role}?auth=${localStorage.getItem("token")}`}
              target="_blank" rel="noreferrer" data-testid={`manual-${role}-link`}
              className="surface p-5 flex gap-4 hover:-translate-y-1 hover:shadow-md hover:border-primary/40 transition-[transform,box-shadow,border-color] duration-200">
              <FileText size={22} className="text-primary shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{title}</p>
                <p className="mt-1 text-xs text-muted-foreground">{desc}</p>
                <p className="mt-2 text-xs font-medium text-primary">PDF with screenshots</p>
              </div>
              <Download size={16} className="text-muted-foreground shrink-0" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
